import { useState } from "react";
import { describeDeskReceipt, isDeskReceiptSuccess } from "../../lib/deskWrites";
import DeskVersionPreview from "./DeskVersionPreview";

/**
 * The app's own line under a Lumen reply: what really happened on the desk, not what Lumen said.
 * While that reply's desk question is still open, the choice is offered right here too — the desk
 * isn't on screen in focus mode or with Lumen maximized. Same for undoing a Replace.
 */
export default function DeskReceiptLine({ receipt, onDark = false, deskChoices = null }) {
  const [undoNote, setUndoNote] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  if (!receipt) return null;
  const landed = isDeskReceiptSuccess(receipt) && receipt.saved !== false;
  const pending = deskChoices?.pending;
  const canChoose = receipt.status === "pending" && !!receipt.pendingId && pending?.id === receipt.pendingId;
  const canUndo = receipt.status === "replaced" && !!receipt.pendingId && deskChoices?.undoableReplaceId === receipt.pendingId;
  const docLabel = pending?.existingTitle ? `“${pending.existingTitle}”` : "that doc";

  const buttonStyle = {
    padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
    background: onDark ? "rgba(255,255,255,0.08)" : "white",
    border: `1px solid ${onDark ? "rgba(125,216,213,0.5)" : "rgba(77,184,181,0.45)"}`,
    color: onDark ? "#7DD8D5" : "#2F8F8C",
  };
  const quietStyle = { ...buttonStyle, border: "1px solid transparent", background: "transparent" };
  const choose = (choice) => deskChoices.resolve(choice);
  const undo = async () => {
    const result = await deskChoices.undoReplace();
    if (!result?.ok) setUndoNote("Can't undo. The doc has changed since.");
  };

  const icon = receipt.status === "undone" ? "↩ " : landed ? "✓ " : "⚠ ";
  const color = landed || receipt.status === "undone" ? (onDark ? "#7DD8D5" : "#2F8F8C") : (onDark ? "#F6C27A" : "#B45309");

  return (
    <div style={{ marginTop: 6, whiteSpace: "normal", fontFamily: "'DM Sans', sans-serif" }}>
      <div role="status" style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.5, color }}>
        {icon}
        {describeDeskReceipt(receipt)}
      </div>
      {canChoose && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {pending.kind === "version" ? (
            <>
              <button type="button" style={buttonStyle} onClick={() => choose("replace")}>Update doc</button>
              <button type="button" style={buttonStyle} onClick={() => choose("new")}>Keep both</button>
              <button type="button" style={quietStyle} onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? "Hide new version" : "See new version"}
              </button>
            </>
          ) : (
            <>
              <button type="button" style={buttonStyle} onClick={() => choose("append")}>Add to {docLabel}</button>
              <button type="button" style={buttonStyle} onClick={() => choose("new")}>Make a new doc</button>
            </>
          )}
          <button type="button" style={quietStyle} onClick={() => choose("dismiss")}>Skip</button>
        </div>
      )}
      {canChoose && showPreview && pending.kind === "version" && (
        <div style={{ marginTop: 6 }}>
          <DeskVersionPreview currentHtml={pending.currentContent} draftHtml={pending.deskUpdate?.content} onDark={onDark} maxHeight={260} />
        </div>
      )}
      {canUndo && (
        <div style={{ marginTop: 6 }}>
          <button type="button" style={buttonStyle} onClick={undo}>Undo</button>
        </div>
      )}
      {undoNote && <div style={{ marginTop: 4, fontSize: 11, color: onDark ? "#F6C27A" : "#B45309" }}>{undoNote}</div>}
    </div>
  );
}
